import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  subscribeNotificationInsert,
  subscribeNotificationUpdate,
} from "@/lib/notificationsBus";

/**
 * Unified LR Direct nav-badge counter.
 * Counts unread notifications of ALL types that represent "a new LR Direct
 * load is available to me":
 *   - private_load_posted     (legacy DB-trigger private-network posts)
 *   - lr_direct_private_load  (broadcast-new-private-load — current path)
 *   - new_open_load           (broadcast-new-open-load — open marketplace)
 */
const TRACKED_TYPES = [
  "private_load_posted",
  "lr_direct_private_load",
  "new_open_load",
] as const;
const TRACKED_SET = new Set<string>(TRACKED_TYPES);

export function useUnreadLRDirectLoads(userId: string | null | undefined) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!userId) {
      setCount(0);
      return;
    }
    let cancelled = false;

    const fetchCount = async () => {
      const { count: c } = await supabase
        .from("notifications")
        .select("id", { count: "exact", head: true })
        .eq("recipient_user_id", userId)
        .in("type", TRACKED_TYPES as unknown as string[])
        .eq("is_read", false);
      if (!cancelled) setCount(c ?? 0);
    };
    fetchCount();

    const offInsert = subscribeNotificationInsert(userId, (row) => {
      const t = (row as { type?: string })?.type;
      if (!t || !TRACKED_SET.has(t)) return;
      setCount((c) => c + 1);
    });
    const offUpdate = subscribeNotificationUpdate(userId, () => {
      fetchCount();
    });

    return () => {
      cancelled = true;
      offInsert();
      offUpdate();
    };
  }, [userId]);

  return count;
}

/** Marks all unread LR Direct load notifications (all tracked types) read. */
export async function markLRDirectLoadsRead(userId: string) {
  await supabase
    .from("notifications")
    .update({ is_read: true, read_at: new Date().toISOString() })
    .eq("recipient_user_id", userId)
    .in("type", TRACKED_TYPES as unknown as string[])
    .eq("is_read", false);
}
